<template>
  <div class="chat-message-options">
    <NebulaButton
      :aria-expanded="dropdownOpen.toString()"
      aria-haspopup="true"
      :aria-label="$t('Actions Menu')"
      class="chat-message-options__toggle-button"
      :class="{
        'chat-message-options__toggle-button--active': dropdownOpen,
      }"
      :disabled="disabled"
      icon-left="ellipses-vertical"
      size="s"
      type="flat"
      @click="toggleDropdown"
    />
    <FocusTrap
      :active="dropdownOpen"
      :initial-focus="() => $refs.dropdownMenu"
      :fallback-focus="() => $refs.dropdownMenu"
      @deactivate="dropdownOpen = false"
    >
      <div
        :aria-hidden="(!dropdownOpen).toString()"
        v-show="dropdownOpen"
        class="chat-message-options__dropdown"
      >
        <ul
          ref="dropdownMenu"
          class="chat-message-options__options-list"
          role="menu"
          tabindex="-1"
        >
          <li class="chat-message-options__option">
            <button
              class="chat-message-options__option-button"
              @click="toggleMessageIsHidden(id)"
            >
              {{ isHidden ? $t('Show Message') : $t('Hide Message') }}
            </button>
          </li>
        </ul>
      </div>
    </FocusTrap>
  </div>
</template>

<script>
import { NebulaButton } from '@discoveryedu/nebula-components';
import { mapActions } from 'pinia';
import { FocusTrap } from 'focus-trap-vue';
import * as types from '@/lib/constants/store';
import useChatStore from '@/stores/chat';

export default {
  name: 'ChatDrawerMessageOptions',
  components: {
    NebulaButton,
    FocusTrap,
  },
  props: {
    disabled: Boolean,
    id: {
      type: String,
      required: true,
    },
    isHidden: Boolean,
  },
  data() {
    return {
      dropdownOpen: false,
    };
  },
  mounted() {
    document.addEventListener('click', this.onBodyClick);
  },
  methods: {
    ...mapActions(useChatStore, [
      types.UPDATE_MESSAGE_IS_HIDDEN,
    ]),
    onBodyClick({ target }) {
      if (!this.$el.contains(target)) {
        this.dropdownOpen = false;
      }
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    },
    toggleMessageIsHidden(id) {
      this[types.UPDATE_MESSAGE_IS_HIDDEN]({ id, isHidden: !this.isHidden });
    },
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onBodyClick);
  },
};
</script>

<style lang="stylus">
.chat-message-options {
  align-self: flex-start;
  position: relative;

  &__toggle-button.nebula-button {
    background-color: rgba($nebula-color-black, .2);
    display: flex;
    margin-block: 0;
    margin-inline: 0 $nebula-space-1x;
    opacity: 0;
    padding: $nebula-space-half;

    svg {
      fill: inherit;
    }

    .chat-message__bubble-item:hover &, &:hover, &:focus, &--active {
      opacity: 1;
    }

    &:hover, &:focus {
      background-color: rgba($nebula-color-black, .3);
    }

    &:active {
      background-color: rgba($nebula-color-black, .4);

      svg {
        fill: inherit;
      }
    }
  }

  &__dropdown {
    background: $comet-background-color-default;
    border-radius: $nebula-border-radius-badge-default;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1), 0px 0px 6px rgba(0, 0, 0, 0.15);
    position: absolute;
    inset-inline-end: $nebula-space-1x;
    top: 100%;
    z-index: 1; // pull dropdown above other chat messages
  }

  &__options-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__option-button {
    background-color: none;
    border: 0;
    border-radius: $nebula-border-radius-badge-default;
    cursor: pointer;
    font-size: $nebula-font-size-body-2;
    padding: $nebula-space-1x $nebula-space-2x $nebula-space-1x $nebula-space-2x;
    transition: background-color $nebula-transition-default;
    white-space: nowrap;

    &:hover, &:focus {
      background: $comet-background-color-light;
    }
  }

  html[dir="rtl"] & {
    &__toggle-button {
      margin-inline: 0 $nebula-space-1x;
    }
  }
}
</style>
